/**
 * Adult-first shared release (CTW-DURMAH-ADULT-FIRST-RELEASE-20260925).
 *
 * Shared private membership is for learners aged 18 and over. The adult
 * statement is recorded as acceptance of this versioned terms/eligibility
 * statement in the existing `profiles.terms_version` + `accepted_terms_at`
 * columns (no migration). Every earlier value, including the column default
 * "v1", is NOT an adult statement: historical or unknown age never becomes
 * one silently, so those members are asked to confirm before a trial or AI.
 * This is a self-declaration, not age assurance or identity verification.
 */
export const SHARED_ADULT_TERMS_VERSION = "shared-uk-18plus-2026-09-25";
export const ADULT_CONFIRMATION_REQUIRED = "adult_confirmation_required";
export const ADULT_ONLY_TASK = "adult_only";
/** Query value carried from the sign-in page through the OAuth callback. */
export const ADULT_ELIGIBILITY_PARAM = "eligibility";
export const ADULT_ELIGIBILITY_VALUE = "18-plus";
export const ADULT_ONLY_MESSAGE = "Durmah is for learners aged 18 and over.";
export const ADULT_CONFIRMATION_MESSAGE = "Please confirm that you are 18 or older to continue. Sign in again from the Durmah sign-in page to confirm.";

export function hasSharedAdultEligibility(profile: { terms_version?: string | null; accepted_terms_at?: string | null } | null | undefined): boolean {
  return profile?.terms_version === SHARED_ADULT_TERMS_VERSION &&
    typeof profile.accepted_terms_at === "string" && Number.isFinite(Date.parse(profile.accepted_terms_at));
}

/**
 * The age statement a new task form may pre-fill. Tasks arrive newest first and
 * only the LATEST valid statement counts. In the shared adult-first release it is
 * reused only when it is 18+: a later 16-17 answer is never skipped in favour of
 * an older 18+ one, so the box stays unconfirmed. Legacy reuses either band.
 */
export function latestReusableAgeBand(tasks: Array<{ age_band?: string | null } | null | undefined>, shared: boolean): "16-17" | "18-plus" | undefined {
  const latest = tasks.map((task) => task?.age_band).find((band): band is "16-17" | "18-plus" => band === "16-17" || band === "18-plus");
  return shared ? (latest === "18-plus" ? latest : undefined) : latest;
}

/**
 * Shared mode has one member entry: the Google sign-in page with the 18+
 * statement. Legacy LNAT login/signup URLs are routed there (with an LNAT
 * destination after sign-in) instead of creating accounts through their own
 * email or LNAT-audience flow.
 */
export function sharedLnatEntryDestination(intent: string | undefined, next: string): string {
  const params = new URLSearchParams();
  if (intent === "signup") params.set("intent", "signup");
  params.set("next", next || "/lnat/dashboard");
  return `/login?${params.toString()}`;
}
